// The Steps card on the Record (spec C8, the Record board): step 1 Dec page (DecPageStep), then
// 2 Check AMS360 or Renew in AMS360, 3 Email insured (EmailStep), 4 Log activity, 5 Proof to bank
// and 6 Done with Close renewal. Steps open in order; ticking stamps who and when, writes the Portal
// log and opens the next step, and the toast offers Undo. Only the last step done can be unticked
// (who ticked it the same day, or the Manager). AMS360 is never written: the steps link to it and
// hand the VA the values to copy. Markup from design/kit (StepCard, Checkbox, Dialog) and the Record
// board (matchrow, ways, cpy, cov, logrow, bankline, sendbox, cprows, ocards, sumlist, ropts).
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import type {
  AmsPathAction,
  Api,
  BankOutcome,
  BranchStepKey,
  ReadingFixField,
  RenewalDetail,
  StepKey,
  StepsView,
  StepTick,
} from '../auth/api';
import { ConfirmPopup } from '../shell/ConfirmPopup';
import { Dialog } from '../shell/Dialog';
import { Tip } from '../shell/Tip';
import { Toast } from '../shell/Toast';
import { takeGlide, useNextStepScroll } from './use-next-step-scroll';
import {
  AlertTriangleIcon,
  BankIcon,
  ClockIcon,
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  ExternalLinkIcon,
  InfoIcon,
  ListChecksIcon,
  PencilIcon,
  RefreshIcon,
} from '../shell/icons';
import { EmailStep } from './EmailStep';
import { BackDialog, BranchDialog } from './BranchDialogs';
import { BranchSteps } from './BranchSteps';
import {
  handNote,
  branchBackText,
  branchRefusal,
  branchSetText,
  branchUndoneText,
  branchUntickText,
} from './branch-lines';
import { StepsPreviouslyDialog, type Marker } from './StepsPreviouslyDialog';
import { FoldChevron } from './StepFold';
import { useStepFold } from './use-step-fold';
import { fillBookmarklet, mciClipboard, mciValues, setUpOnce } from './mycoverageinfo';
import { showValue } from './fix-lines';
import { longDate, nyToday } from './renewal-lines';
import {
  anywayReason,
  backToCheckToast,
  bankOutcomeOrder,
  closeAnywayLine,
  manualToast,
  notDownloadedLine,
  recheckLine,
  recheckTag,
  waitsForDownload,
  closeAnywayTitle,
  decDone,
  decPageSummary,
  missingNote,
  missingOf,
  previouslyToast,
  previouslyUndoneToast,
  stepStamp,
  bankHidden,
  doneSteps,
  lockedLine,
  isClosedView,
  myCoverageInfoUrl,
  noAmsPremiumLine,
  notDecPageLine,
  refreshText,
  bankFilesNote,
  borrowerLine,
  outcomeLabels,
  outcomeSub,
  outcomeLine,
  closedLine,
  payFirstNote,
  refusalText,
  shownSteps,
  stepNumberOf,
  stepTitle,
  stepWhy,
  tickToast,
  undoTip,
  undoToast,
} from './step-lines';

type Props = {
  api: Api;
  renewal: RenewalDetail;
  /** Step 1, the Dec page step. */
  first: ReactNode;
  /** What the steps are built from (the dec page and its reading): a change asks again. */
  revision: string;
  /** Opens the confirm or correct dialog for a dec page value (spec C10). */
  onCheck: (field: ReadingFixField) => void;
  /** Steps already in hand (tests); otherwise asked for when the card opens. */
  initial?: StepsView;
  /** Told when the renewal's status changed (Close renewal, Reopen, Premium received). */
  onStatusChange?: () => void;
  /** Told each time the steps change (a tick, a send, a new dec page or reading). */
  onView?: (v: StepsView) => void;
  /** The Record's "..." menu asked for Close renewal or Mark steps done previously (S16). */
  ask?: StepsAsk | null;
  /** The dialog the menu asked for closed. */
  onAskDone?: () => void;
  /** Who is signed in, for Mark steps done previously. */
  me?: Marker;
  /** Back to Check AMS360 from the Record's "..." menu. */
  actions?: Ref<StepsActions>;
  /** Hand to AE with the reason Non-renewal and the note, for non-renewal step 4 (S17). */
  onHandNonRenewal?: (note: string) => void;
};

/** What the Record's "..." menu asks the Steps card to open; `n` tells one ask from the next. */
export type StepsAsk = {
  what: 'close' | 'previously' | 'non_renewal' | 'inactive' | 'back';
  n: number;
};

/** What the Record's "..." menu does on the Steps card at once (S16 Batch 3). */
export type StepsActions = { backToCheck: () => void };

/** The toast's Undo: a step, Premium received, or step 2's Recheck tomorrow and Renew manually. */
type UndoKey =
  | StepKey
  | 'paid'
  | 'recheck'
  | 'manual'
  | 'check'
  | 'branch_set'
  | 'branch_back'
  | `branch:${BranchStepKey}`;
type Say = (text: string, undo?: UndoKey) => void;

export function RecordSteps({
  api,
  renewal,
  first,
  revision,
  onCheck,
  initial,
  onStatusChange,
  onView,
  ask = null,
  onAskDone,
  me,
  actions,
  onHandNonRenewal,
}: Props) {
  const [view, setView] = useState<StepsView | null>(initial ?? null);
  // The page's Mortgagee card and Portal log follow the steps; the view it loaded with is its own.
  // Only a view this card read is reported, once: the page reading its steps again (the auto
  // refresh) must not bring back this card's older copy as a new one (Codex on 292).
  const reported = useRef(initial ?? null);
  useEffect(() => {
    if (!view || view === reported.current) return;
    reported.current = view;
    onView?.(view);
  }, [view, onView]);
  const [failed, setFailed] = useState(false);
  const [toast, setToast] = useState<{
    id: number;
    text: string;
    undo?: UndoKey;
    /** The toast's Undo takes back this Mark steps done previously. */
    batch?: string;
  } | null>(null);
  const [closing, setClosing] = useState(false);

  // Asked for when the card opens, when the dec page or its reading changes, and after a send.
  const [asked, setAsked] = useState(0);
  const load = useCallback(() => setAsked((n) => n + 1), []);
  // After a tick, an Undo or Mark as sent, glide to the step that is now open instead of jumping.
  const toNextStep = useNextStepScroll();
  // Mark as sent ticks the email step on the server, so its reload glides like a tick does.
  const glideOnLoad = useRef(false);
  // The steps the page loaded with are already current for the revision it opened with.
  const [loadedFor] = useState(() => (initial ? `${revision}|0` : null));
  useEffect(() => {
    if (`${revision}|${asked}` === loadedFor) return;
    let live = true;
    api
      .steps(renewal.id)
      .then((v) => {
        if (!live) return;
        takeGlide(glideOnLoad, toNextStep);
        setView(v);
        setFailed(false);
      })
      .catch(() => {
        if (!live) return;
        glideOnLoad.current = false;
        setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [api, renewal.id, revision, asked, loadedFor, toNextStep]);

  const say: Say = (text, undo) => setToast({ id: Date.now(), text, undo });
  async function tick(t: StepTick): Promise<boolean> {
    const res = await api.tickStep(renewal.id, t);
    if (res.ok) {
      toNextStep();
      setView(res.body.steps);
      say(
        'skip' in t ? 'Email skipped.' : tickToast(t.step, res.body.steps.amsStep, res.body.steps),
        t.step,
      );
      if (t.step === 'done') onStatusChange?.();
      return true;
    }
    const sent = res.body.steps as StepsView | undefined;
    if (sent) setView(sent);
    say(refusalText(res.error));
    return false;
  }

  async function undo(step: StepKey) {
    const res = await api.undoStep(renewal.id, step);
    if (res.ok) {
      toNextStep();
      setView(res.body.steps);
      say(undoToast(step, res.body.steps.amsStep));
      if (step === 'done') onStatusChange?.();
      return;
    }
    const sent = res.body.steps as StepsView | undefined;
    if (sent) setView(sent);
    say(refusalText(res.error));
  }

  const [refreshing, setRefreshing] = useState(false);
  async function refresh() {
    setRefreshing(true);
    const res = await api.refreshAms(renewal.id);
    setRefreshing(false);
    const sent = res.body.steps as StepsView | undefined;
    if (sent) setView(sent);
    if (res.ok) {
      say(refreshText(res.body.found, res.body.steps));
      if (res.body.found) onStatusChange?.();
    } else say(refusalText(res.error));
  }

  async function undoPreviously(batch: string) {
    const res = await api.undoStepsPreviously(renewal.id, batch);
    const sent = res.body.steps as StepsView | undefined;
    if (sent) setView(sent);
    if (res.ok) {
      toNextStep();
      setView(res.body.steps);
      say(previouslyUndoneToast);
    } else say(refusalText(res.error));
  }

  /** Step 2 with no new term in AMS360 (S16): Recheck tomorrow, renew by hand, and their Undo. */
  async function amsPath(action: AmsPathAction) {
    const res = await api.amsPath(renewal.id, action);
    const sent = res.body.steps as StepsView | undefined;
    if (sent) setView(sent);
    if (!res.ok) return say(refusalText(res.error));
    const v = res.body.steps;
    if (action === 'recheck' && v.download?.recheck)
      say(recheckLine(v.download.recheck), 'recheck');
    else if (action === 'manual') say(manualToast, 'manual');
    else if (action === 'check') say(backToCheckToast, 'check');
    else say('Recheck taken back.');
  }

  // "..." Back to Check AMS360: the Record's menu calls it directly, with the toast and Undo here.
  useImperativeHandle(actions, () => ({ backToCheck: () => void amsPath('check') }));

  async function paid() {
    const res = await api.premiumReceived(renewal.id);
    if (res.ok) {
      setView(res.body.steps);
      say('Premium received. Status: Done.', 'paid');
      onStatusChange?.();
    } else say(refusalText(res.error));
  }

  async function undoPaid() {
    const res = await api.undoPremiumReceived(renewal.id);
    const sent = res.body.steps as StepsView | undefined;
    if (sent) setView(sent);
    if (res.ok) {
      say('Undone. Status: Need payment.');
      onStatusChange?.();
    } else say(refusalText(res.error));
  }

  /** The branch's switch and Back (S17): the header's tag follows, so the Record is read again. */
  async function branchDone(
    res: Awaited<ReturnType<Api['setBranch']>>,
    text: string,
    undoKey?: UndoKey,
  ): Promise<true | string> {
    const sent = res.body.steps as StepsView | undefined;
    if (sent) setView(sent);
    if (!res.ok) return branchRefusal(res.error);
    toNextStep();
    say(text, undoKey);
    onAskDone?.();
    onStatusChange?.();
    return true;
  }
  async function undoBranch(action: 'set' | 'back') {
    const res = await api.undoBranch(renewal.id, action);
    const sent = res.body.steps as StepsView | undefined;
    if (sent) setView(sent);
    say(res.ok ? branchUndoneText : branchRefusal(res.error));
    if (res.ok) onStatusChange?.();
  }
  async function untickBranch(step: BranchStepKey) {
    const res = await api.undoBranchStep(renewal.id, step);
    const sent = res.body.steps as StepsView | undefined;
    if (sent) setView(sent);
    say(res.ok ? branchUntickText(step) : branchRefusal(res.error));
    if (res.ok && step === 'pi_close') onStatusChange?.();
  }

  const ctx = { view: view!, tick, undo, say, refresh, refreshing, amsPath };
  const branch = view?.branch ?? null;
  return (
    <section className="card steps" aria-label="Steps">
      <div className="card-h">
        <h2 className="card-t">
          <ListChecksIcon className="ico s14" />
          Steps
        </h2>
        {view && <StepsProgress done={doneSteps(view)} />}
      </div>
      {!branch && first}
      {failed && !view && (
        <div className="step locked" aria-disabled="true">
          <div className="step-h">
            <span className="st-desc t12 amber">
              The steps could not load. Reload the page to try again.
            </span>
          </div>
        </div>
      )}
      {view && branch && (
        <BranchSteps
          api={api}
          renewal={renewal}
          view={{ ...view, branch }}
          revision={revision}
          onView={(v) => {
            toNextStep();
            setView(v);
            // The Policy inactive close changes the status in the header.
            if (v.close.renewalStatus !== view.close.renewalStatus) onStatusChange?.();
          }}
          say={(text, step) => say(text, step ? `branch:${step}` : undefined)}
          onHand={onHandNonRenewal ? () => onHandNonRenewal(handNote(branch)) : undefined}
          onSent={() => {
            glideOnLoad.current = true;
            load();
          }}
        />
      )}
      {/* Closed while on the branch by the AE (Finish handoff): Reopen, as on the renewal steps
          (Codex on PR 315). */}
      {view &&
        branch &&
        view.steps.done.state === 'done' &&
        view.steps.done.details.source !== 'branch' && (
          <DoneStep ctx={ctx} onClose={() => {}} onPaid={() => void paid()} />
        )}
      {view && !branch && (
        <>
          <AmsStep ctx={ctx} onCheck={onCheck} />
          <EmailStep
            key={renewal.id}
            api={api}
            renewal={renewal}
            ready={view.steps.email.state !== 'locked'}
            closed={isClosedView(view)}
            revision={revision}
            step={view.steps.email}
            lockedText={lockedLine(view, 'email')}
            onSent={() => {
              glideOnLoad.current = true;
              load();
            }}
            onMarkDone={() => void tick({ step: 'email' })}
            onSkip={(reason) => tick({ step: 'email', skip: true, reason })}
            undo={undoTip(view, 'email') ? null : () => void undo('email')}
          />
          <ActivityStep ctx={ctx} />
          {/* No mortgagee: Proof to bank is not shown at all (Yehuda, Oct 29). */}
          {!bankHidden(view) && <BankStep ctx={ctx} api={api} />}
          <DoneStep ctx={ctx} onClose={() => setClosing(true)} onPaid={() => void paid()} />
        </>
      )}
      {(closing || ask?.what === 'close') && view && (
        <CloseDialog
          view={view}
          renewal={renewal}
          onClose={() => {
            setClosing(false);
            onAskDone?.();
          }}
          onConfirm={async (status, sheetComment, reason) => {
            const t: StepTick = reason
              ? { step: 'done', status, sheetComment, anyway: true, reason }
              : { step: 'done', status, sheetComment };
            if (await tick(t)) {
              setClosing(false);
              onAskDone?.();
            }
          }}
        />
      )}
      {(ask?.what === 'non_renewal' || ask?.what === 'inactive') && view && (
        <BranchDialog
          r={renewal}
          branch={ask.what}
          onClose={() => onAskDone?.()}
          onSave={async (b) =>
            branchDone(
              await api.setBranch(renewal.id, { branch: ask.what as 'non_renewal', ...b }),
              branchSetText(ask.what as 'non_renewal'),
              'branch_set',
            )
          }
        />
      )}
      {ask?.what === 'back' && view?.branch && (
        <BackDialog
          r={renewal}
          branch={view.branch.branch}
          onClose={() => onAskDone?.()}
          onSave={async (reason) =>
            branchDone(await api.leaveBranch(renewal.id, reason), branchBackText, 'branch_back')
          }
        />
      )}
      {ask?.what === 'previously' && view && me && (
        <StepsPreviouslyDialog
          api={api}
          view={view}
          renewal={renewal}
          me={me}
          onClose={() => onAskDone?.()}
          onSaved={({ batch, steps, numbers }) => {
            toNextStep();
            setView(steps);
            setToast({ id: Date.now(), text: previouslyToast(numbers), batch });
            onAskDone?.();
          }}
        />
      )}
      {toast && (
        <Toast
          key={toast.id}
          text={toast.text}
          action={
            toast.batch
              ? { label: 'Undo', onClick: () => void undoPreviously(toast.batch!) }
              : toast.undo
                ? {
                    label: 'Undo',
                    onClick: () =>
                      void (toast.undo === 'branch_set'
                        ? undoBranch('set')
                        : toast.undo === 'branch_back'
                          ? undoBranch('back')
                          : toast.undo?.startsWith('branch:')
                            ? untickBranch(toast.undo.slice(7) as BranchStepKey)
                            : toast.undo === 'paid'
                              ? undoPaid()
                              : toast.undo === 'recheck'
                                ? amsPath('recheck_undo')
                                : toast.undo === 'manual'
                                  ? amsPath('check')
                                  : toast.undo === 'check'
                                    ? amsPath('manual')
                                    : undo(toast.undo as StepKey)),
                  }
                : undefined
          }
        />
      )}
    </section>
  );
}

type Ctx = {
  view: StepsView;
  tick: (t: StepTick) => Promise<boolean>;
  undo: (step: StepKey) => Promise<void>;
  say: Say;
  /** Refresh from AMS360: reads the renewal's new term now instead of after the nightly sync. */
  refresh: () => Promise<void>;
  refreshing: boolean;
  /** Step 2 with no new term in AMS360 (S16): Recheck tomorrow, Renew manually instead. */
  amsPath: (action: AmsPathAction) => Promise<void>;
};

/** The Steps card's progress, top right (Record board): one bar per step, "2 of 6 done". */
function StepsProgress({ done }: { done: boolean[] }) {
  const n = done.filter(Boolean).length;
  return (
    <div className="row g8" role="img" aria-label={`${n} of ${done.length} steps done`}>
      <div className="prog" aria-hidden="true">
        {done.map((d, i) => (
          <span key={i} className={d ? 'on' : undefined} />
        ))}
      </div>
      <span className="t12 text2 w5" aria-hidden="true">
        {n} of {done.length} done
      </span>
    </div>
  );
}

/** The step header (kit StepCard): number or check, title, why, stamp. */
function StepShell({
  ctx,
  k,
  tag,
  stamp,
  children,
}: {
  ctx: Ctx;
  k: StepKey;
  tag?: ReactNode;
  stamp?: ReactNode;
  children?: ReactNode;
}) {
  const s = ctx.view.steps[k];
  const done = s.state === 'done';
  const now = s.state === 'open' || s.state === 'waiting';
  const locked = !done && !now;
  const n = stepNumberOf(ctx.view, k);
  const title = stepTitle(k, ctx.view.amsStep);
  // Done steps fold to one line; the current one is open (StepCard). Done (the last step) stays
  // open: after closing it holds Premium received and Reopen.
  const fold = useStepFold(s.state, now || k === 'done');
  const foldable = !locked && !!children;
  return (
    <div
      className={done ? 'step' : now ? 'step now' : 'step locked'}
      aria-disabled={locked}
      aria-label={`Step ${n}, ${title}`}
    >
      <div className="step-h" onClick={foldable ? fold.toggle : undefined}>
        <span className={done ? 'num num-done' : now ? 'num num-now' : 'num num-next'}>
          {done ? <CheckIcon className="ico s12" /> : n}
        </span>
        <span className="st-t">
          <span className="st-tr">
            <span className={done ? 'st-title' : now ? 'st-title now' : 'st-title next'}>
              {title}
            </span>
            {tag}
          </span>
          <span className="st-desc t12 muted">
            {locked
              ? lockedLine(ctx.view, k)
              : k === 'ams360' && waitsForDownload(ctx.view)
                ? 'AMS360 has no new term yet.'
                : stepWhy(k, ctx.view.amsStep)}
          </span>
        </span>
        {stamp ??
          (s.stamp && (
            <span className={s.details.accepted ? 'stamp now' : 'stamp ok'}>{stepStamp(s)}</span>
          ))}
        {foldable && <FoldChevron open={fold.open} onToggle={fold.toggle} title={title} />}
      </div>
      {foldable && fold.open && <div className="step-b">{children}</div>}
    </div>
  );
}

/** The checkbox that ticks a step, unticked again only when it is the last step done. */
function TickBox({
  ctx,
  k,
  label,
  ready,
  why,
  onTick,
}: {
  ctx: Ctx;
  k: StepKey;
  label: string;
  /** May be ticked now. */
  ready: boolean;
  /** Why it cannot be ticked yet. */
  why?: string;
  onTick: () => void;
}) {
  const s = ctx.view.steps[k];
  const done = s.state === 'done';
  const tip = done ? undoTip(ctx.view, k) : !ctx.view.canWork ? undoTip(ctx.view, k) : why;
  const off = done ? !!tip : !ready || !ctx.view.canWork;
  const box = (
    <label className="ckline">
      <input
        type="checkbox"
        checked={done}
        disabled={off}
        onChange={() => (done ? void ctx.undo(k) : onTick())}
      />
      {label}
    </label>
  );
  return tip && off ? (
    <Tip text={tip} focusable>
      {box}
    </Tip>
  ) : (
    box
  );
}

function useCopy(say: Say) {
  const [copied, setCopied] = useState<string | null>(null);
  return {
    copied,
    copy: async (key: string, value: string, what: string) => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(key);
        say(`${what} copied.`);
      } catch {
        say('Could not copy. Select the text and copy it by hand.');
      }
    },
  };
}

function CopyButton({
  label,
  value,
  k,
  c,
}: {
  label: string;
  value: string;
  k: string;
  c: ReturnType<typeof useCopy>;
}) {
  return (
    <button
      type="button"
      className="ibtn"
      aria-label={`Copy ${label}`}
      onClick={() => void c.copy(k, value, label)}
    >
      {c.copied === k ? <span className="cpd">Copied</span> : <CopyIcon className="ico s14" />}
    </button>
  );
}

const premiumText = (n: number | null) =>
  n === null ? 'Not known yet' : showValue('totalPremium', n);

function OpenInAms({ href, primary = false }: { href: string; primary?: boolean }) {
  return (
    <a
      className={primary ? 'btn btn-primary' : 'btn btn-sm'}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      <ExternalLinkIcon className={primary ? 'ico s14' : 'ico s12'} />
      Open in AMS360
    </a>
  );
}

// ---------- step 2 ----------

function AmsStep({ ctx, onCheck }: { ctx: Ctx; onCheck: (f: ReadingFixField) => void }) {
  const v = ctx.view;
  const s = v.steps.ams360;
  const [accepting, setAccepting] = useState(false);
  const [reason, setReason] = useState('');
  const [checks, setChecks] = useState({
    renewClicked: false,
    policyNumberSet: false,
    coveragesChecked: false,
    premiumEntered: false,
  });
  const c = useCopy(ctx.say);

  // A dec page that could not be read: its values are typed first, under Prior term vs renewal.
  const needsTyping =
    v.decPage.reading === 'failed' &&
    (v.premiums.dec === null || !v.renew.policyNumber || !v.renew.effectiveDate);
  const waitChecks = needsTyping || v.openChecks.length > 0;
  const holdLine = refusalText(needsTyping ? 'typed_values_missing' : 'checks_open');
  const reading = v.decPage.reading === 'queued' || v.decPage.reading === 'running';
  let body: ReactNode;

  if (s.state !== 'done' && v.decPage.notDecPage) {
    body = (
      <div className="anote">
        <AlertTriangleIcon className="ico s14" />
        <span>{notDecPageLine(v.decPage.notDecPage)}</span>
      </div>
    );
  } else if (s.state === 'done') {
    body =
      typeof s.details.accepted === 'string' ? (
        <span className="okline amber">
          <AlertTriangleIcon className="ico s14" />
          <span>Difference accepted: {s.details.accepted}</span>
        </span>
      ) : null;
  } else if (waitsForDownload(v)) {
    // Path A, nothing downloaded yet (Record board, STEP 2 PREMIUM COMPARE; S16 Batch 3).
    const recheck = v.download?.recheck ?? null;
    body = (
      <>
        <div className="matchrow">
          <div>
            <span>AMS360, new term</span>
            <b>Not downloaded yet</b>
            <em>Nightly sync</em>
          </div>
          <div>
            <span>Dec page</span>
            <b className="mono">{premiumText(v.premiums.dec)}</b>
          </div>
          <span className="tag t-amber">
            <ClockIcon className="ico s12" />
            Not downloaded yet
          </span>
        </div>
        <p className="m0 t12 muted">{notDownloadedLine}</p>
        {recheck && (
          <span className="okline amber">
            <ClockIcon className="ico s14" />
            {recheckLine(recheck)}
          </span>
        )}
        <div className="row g8 wrap">
          <button
            type="button"
            className="btn"
            disabled={!v.canWork}
            onClick={() => void ctx.amsPath('recheck')}
          >
            Recheck tomorrow
          </button>
          <button
            type="button"
            className="btn"
            disabled={!v.canWork}
            onClick={() => void ctx.amsPath('manual')}
          >
            Renew manually instead
          </button>
        </div>
      </>
    );
  } else if (v.amsStep === 'check') {
    const { ams, dec, match } = v.premiums;
    // AMS360 shows the policy Renewed, but its new term premium has not come through yet.
    const noAms = ams === null && dec !== null && !reading;
    const diff = ams !== null && dec !== null ? dec - ams : null;
    body = (
      <>
        <div className="matchrow">
          <div>
            <span>AMS360 renewed term</span>
            <b className="mono">{premiumText(ams)}</b>
          </div>
          <div>
            <span>Dec page</span>
            <b className="mono">{premiumText(dec)}</b>
          </div>
          {match === true && <span className="tag t-green">Match</span>}
          {match === false && diff !== null && (
            <span className="tag t-amber">
              Dec page is {showValue('totalPremium', Math.abs(diff))}{' '}
              {diff > 0 ? 'higher' : 'lower'}
            </span>
          )}
          <a
            className="linkbtn ml-auto"
            href={v.links.decPage}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open dec page in AMS360
          </a>
        </div>
        {reading && (
          <span className="readline">
            <span className="spin" />
            Reading the dec page
          </span>
        )}
        {waitChecks && (
          <div className="anote">
            <AlertTriangleIcon className="ico s14" />
            <span>{holdLine}</span>
          </div>
        )}
        {!reading && dec === null && !waitChecks && (
          <p className="m0 t12 muted">
            Enter the dec page values in step 1 to compare the premium.
          </p>
        )}
        {(match === false || noAms) && !waitChecks && v.canWork && (
          <div className="ways">
            <span className="t12 text2">{noAms ? noAmsPremiumLine : 'Pick one way out:'}</span>
            <div className="row g8 wrap">
              {noAms ? (
                <RefreshButton ctx={ctx} />
              ) : (
                <button
                  type="button"
                  className="btn btn-sm"
                  aria-haspopup="dialog"
                  onClick={() => onCheck('totalPremium')}
                >
                  <PencilIcon className="ico s12" />
                  Fix dec page value
                </button>
              )}
              <OpenInAms href={v.links.customer} />
              {!noAms && <RefreshButton ctx={ctx} />}
              <button
                type="button"
                className="btn btn-sm"
                aria-expanded={accepting}
                onClick={() => setAccepting(!accepting)}
              >
                {noAms ? "Accept without AMS360's premium" : 'Accept the difference'}
              </button>
            </div>
            {accepting && (
              <div className="col g8">
                <label className="flab m0" htmlFor="accept-reason">
                  Reason <span className="req">*</span>
                </label>
                <textarea
                  id="accept-reason"
                  className="input"
                  rows={2}
                  style={{ height: 'auto' }}
                  maxLength={300}
                  placeholder={
                    noAms
                      ? 'For example: checked the premium in AMS360, matches'
                      : 'For example: AMS360 adds the policy fee'
                  }
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <div className="row endj">
                  <button
                    type="button"
                    className={reason.trim() ? 'btn btn-primary' : 'btn'}
                    disabled={!reason.trim()}
                    onClick={() => void ctx.tick({ step: 'ams360', mode: 'accept', reason })}
                  >
                    Accept and mark step done
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
        {match !== false && !noAms && (
          <div className="row endj">
            <TickBox
              ctx={ctx}
              k="ams360"
              label="Premium matches"
              ready={match === true && !waitChecks}
              why={waitChecks ? holdLine : 'The dec page premium is needed to compare.'}
              onTick={() => void ctx.tick({ step: 'ams360', mode: 'match' })}
            />
          </div>
        )}
      </>
    );
  } else {
    const r = v.renew;
    const rows: { k: string; label: string; value: string | null; mono?: boolean }[] = [
      { k: 'pol', label: 'New policy number', value: r.policyNumber, mono: true },
      {
        k: 'prem',
        label: 'Renewal premium',
        value: r.premium === null ? null : premiumText(r.premium),
        mono: true,
      },
      {
        k: 'term',
        label: 'Term',
        value:
          r.effectiveDate && r.expirationDate
            ? `${longDate(r.effectiveDate)} to ${longDate(r.expirationDate)}`
            : null,
      },
      { k: 'exec', label: 'Executive', value: r.executiveCode, mono: true },
      { k: 'rep', label: 'Rep', value: r.repCode, mono: true },
    ];
    const items: [keyof typeof checks, string][] = [
      ['renewClicked', 'Renew clicked'],
      ['policyNumberSet', 'Policy number set'],
      ['coveragesChecked', 'Coverages checked'],
      ['premiumEntered', 'Premium entered'],
    ];
    const all = Object.values(checks).every(Boolean);
    body = (
      <>
        <div className="row g8 wrap between">
          <OpenInAms href={v.links.customer} primary />
          <span className="t12 muted">Opens the Policies view for this customer</span>
        </div>
        {reading && (
          <span className="readline">
            <span className="spin" />
            Reading the dec page
          </span>
        )}
        {waitChecks && (
          <div className="anote">
            <AlertTriangleIcon className="ico s14" />
            <span>{holdLine}</span>
          </div>
        )}
        <div>
          <div className="flab">Copy into AMS360</div>
          <div className="cpy">
            {rows.map((row) => (
              <div key={row.k} className="cpy-row">
                <span className="cpy-l">{row.label}</span>
                <span className="cpy-v">
                  <span className={row.value ? (row.mono ? 'mono' : '') : 'faint'}>
                    {row.value ?? 'Not on the dec page'}
                  </span>
                  {row.k === 'pol' && row.value && <PolicyNumberTag value={row.value} r={r} />}
                </span>
                {row.value ? (
                  <CopyButton label={row.label} value={row.value} k={row.k} c={c} />
                ) : (
                  <span />
                )}
              </div>
            ))}
            <p className="cpy-note">If blank in AMS360, use the department default in Settings.</p>
          </div>
        </div>
        {r.coverages.length > 0 && (
          <div>
            <div className="flab">Coverages from the dec page</div>
            <div className="cov">
              <div className="cov-h">
                <span>Coverage</span>
                <span>Limit</span>
                <span>Deductible</span>
              </div>
              {r.coverages.map((cv, i) => (
                <div key={`${cv.name}-${i}`} className="cov-r">
                  <span>{cv.name}</span>
                  <span className="cov-v">{cv.limit || 'Included'}</span>
                  <span className="cov-v">{cv.deductible}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="amsck">
          <span className="t12 text2 grow">
            After you renew, read AMS360 back: this step turns into Check AMS360 with its premium.
          </span>
          {v.canWork && <RefreshButton ctx={ctx} />}
        </div>
        <div>
          <div className="flab">Before you mark it done</div>
          <div className="row g8 wrap">
            {items.map(([k, label]) => (
              <label key={k} className="ckline">
                <input
                  type="checkbox"
                  checked={checks[k]}
                  disabled={!v.canWork}
                  onChange={() => setChecks({ ...checks, [k]: !checks[k] })}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
        <p className="m0 t12 muted">
          If AMS360 downloads the renewal overnight instead, this step turns into Check AMS360 after
          the next sync.
        </p>
        {v.canWork && (
          <div className="row endj">
            <button
              type="button"
              className={all && !waitChecks && !reading ? 'btn btn-primary' : 'btn'}
              disabled={!all || waitChecks || reading}
              onClick={() => void ctx.tick({ step: 'ams360', checks })}
            >
              Mark step done
            </button>
          </div>
        )}
      </>
    );
  }
  return (
    <StepShell
      ctx={ctx}
      k="ams360"
      tag={
        v.amsStep === 'renew' ? (
          <span className="tag t-grey">Automated in Phase 3</span>
        ) : waitsForDownload(v) && v.download?.recheck ? (
          <span className="tag t-amber">{recheckTag(v.download.recheck)}</span>
        ) : null
      }
    >
      {body}
      {s.state === 'done' && v.amsStep === 'renew' && (
        <div className="row endj">
          <TickBox ctx={ctx} k="ams360" label="Renewed in AMS360" ready onTick={() => {}} />
        </div>
      )}
      {s.state === 'done' && v.amsStep === 'check' && (
        <div className="row endj">
          <TickBox
            ctx={ctx}
            k="ams360"
            label={s.details.accepted ? 'Difference accepted' : 'Premium matches'}
            ready
            onTick={() => {}}
          />
        </div>
      )}
    </StepShell>
  );
}

/**
 * Next to the new policy number: changed from the prior term, or, for a carrier that changes the
 * number each term (SageSure), red when it is still the old one (spec C8 B2).
 */
function PolicyNumberTag({ value, r }: { value: string; r: StepsView['renew'] }) {
  const same =
    value.replace(/[^A-Za-z0-9]/g, '') === r.priorPolicyNumber.replace(/[^A-Za-z0-9]/g, '');
  if (same && r.renumbers) {
    return <span className="tag t-red">Same as last term: this carrier changes it each term</span>;
  }
  return same ? null : <span className="tag t-blue">Changed from prior term</span>;
}

function RefreshButton({ ctx }: { ctx: Ctx }) {
  if (isClosedView(ctx.view)) return null;
  return (
    <button
      type="button"
      className="btn btn-sm"
      disabled={ctx.refreshing}
      onClick={() => void ctx.refresh()}
    >
      {ctx.refreshing ? <span className="spin" /> : <RefreshIcon className="ico s12" />}
      {ctx.refreshing ? 'Reading AMS360' : 'Refresh from AMS360'}
    </button>
  );
}

// ---------- step 4 ----------

function ActivityStep({ ctx }: { ctx: Ctx }) {
  const v = ctx.view;
  const s = v.steps.activity;
  const [dragged, setDragged] = useState(false);
  const c = useCopy(ctx.say);
  const done = s.state === 'done';
  return (
    <StepShell
      ctx={ctx}
      k="activity"
      tag={<span className="tag t-grey">Automated in Phase 2</span>}
    >
      {done ? (
        <div className="row endj">
          <TickBox ctx={ctx} k="activity" label="Logged in AMS360" ready onTick={() => {}} />
        </div>
      ) : (
        <ActivityBody ctx={ctx} dragged={dragged} setDragged={setDragged} c={c} />
      )}
    </StepShell>
  );
}

function ActivityBody({
  ctx,
  dragged,
  setDragged,
  c,
}: {
  ctx: Ctx;
  dragged: boolean;
  setDragged: (on: boolean) => void;
  c: ReturnType<typeof useCopy>;
}) {
  const v = ctx.view;
  const skipped = v.steps.email.details.skipped === true;
  return (
    <>
      <div className="row g8 wrap">
        <OpenInAms href={v.links.customer} />
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => void c.copy('pol4', v.activity.policyNumber, 'Policy number')}
        >
          <CopyIcon className="ico s12" />
          {c.copied === 'pol4' ? 'Copied' : 'Copy policy number'}
        </button>
        <span className="mono t12 muted">{v.activity.policyNumber}</span>
      </div>
      <div className="logrow">
        <span className="t12 muted">Description to paste</span>
        <span className="mono ink grow">{v.activity.description}</span>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => void c.copy('log', v.activity.description, 'Description')}
        >
          {c.copied === 'log' ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="row g8 endj">
        {/* Email insured skipped (S17 extras): no email to drag in. */}
        {skipped ? (
          <span className="t12 muted">Email skipped: nothing to drag in.</span>
        ) : (
          <label className="ckline">
            <input
              type="checkbox"
              checked={dragged}
              disabled={!v.canWork}
              onChange={() => setDragged(!dragged)}
            />
            Email dragged in
          </label>
        )}
        <TickBox
          ctx={ctx}
          k="activity"
          label="Logged in AMS360"
          ready={dragged || skipped}
          why="Drag the email into the AMS360 activity first, then tick Email dragged in."
          onTick={() => void ctx.tick({ step: 'activity', emailDraggedIn: true })}
        />
      </div>
    </>
  );
}

// ---------- step 5 ----------

/**
 * The Fill MyCoverageInfo bookmark (renewals/mycoverageinfo.ts): dragged once onto the bookmarks bar,
 * then clicked on MyCoverageInfo's Agent Search page. React refuses javascript: links, so the address
 * is put on the link after it renders; a click here only explains what to do.
 */
function FillBookmark() {
  const [help, setHelp] = useState(false);
  const ref = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    ref.current?.setAttribute('href', fillBookmarklet);
  }, []);
  return (
    <div className="col g6">
      <div className="row g8 wrap">
        <Tip text="Drag this onto your bookmarks bar, then click it on MyCoverageInfo's Agent Search page.">
          <a
            ref={ref}
            className="btn btn-sm"
            draggable
            onClick={(e) => {
              e.preventDefault();
              setHelp(true);
            }}
          >
            <BankIcon className="ico s12" />
            Fill MyCoverageInfo
          </a>
        </Tip>
        <button
          type="button"
          className="linkbtn t12"
          aria-expanded={help}
          onClick={() => setHelp(!help)}
        >
          Set up once
        </button>
      </div>
      {help && (
        <div className="bnr info">
          <InfoIcon />
          <ol className="m0 t12">
            {setUpOnce.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function BankStep({ ctx, api }: { ctx: Ctx; api: Api }) {
  const v = ctx.view;
  const s = v.steps.bank;
  const waiting = s.state === 'waiting';
  const [outcome, setOutcome] = useState<BankOutcome | null>(null);
  // Submitted on starts at today (New York), the usual case; never later than today (the server agrees).
  const today = nyToday(new Date());
  const [date, setDate] = useState(today);
  const [reason, setReason] = useState('');
  const [updated, setUpdated] = useState(false);
  const c = useCopy(ctx.say);
  const mci = mciValues(v.bank.rows);

  if (s.state === 'skipped') {
    return <StepShell ctx={ctx} k="bank" stamp={<span className="stamp">Not needed</span>} />;
  }

  const bank = v.bank.mortgagees[0];
  const others = v.bank.mortgagees.slice(1);
  const typedDate = /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= today ? date : null;
  const ready =
    !!outcome &&
    (outcome !== 'mycoverageinfo' || !!typedDate) &&
    (outcome !== 'not_needed' || !!reason.trim()) &&
    (!waiting || updated || outcome === 'loan_not_found');
  const choices = bankOutcomeOrder.filter((o) => !(waiting && o === 'loan_not_found'));

  async function save() {
    if (!outcome) return;
    const ok = await ctx.tick({
      step: 'bank',
      outcome,
      ...(outcome === 'mycoverageinfo' && typedDate ? { date: typedDate } : {}),
      ...(outcome === 'not_needed' ? { reason } : {}),
      ...(waiting ? { updatedInAms: updated } : {}),
    });
    if (ok) setOutcome(null);
  }

  return (
    <StepShell
      ctx={ctx}
      k="bank"
      tag={
        <>
          <span className="tag t-grey">Automated in Phase 2</span>
          {waiting && <span className="tag t-amber">Waiting on insured for bank info</span>}
        </>
      }
      stamp={
        s.state === 'done' && s.stamp ? (
          <span className="stamp ok">{stepStamp(s)}</span>
        ) : waiting && s.stamp ? (
          <span className="stamp now">Asked {stepStamp(s)}</span>
        ) : undefined
      }
    >
      {s.state === 'done' ? (
        <div className="okbox">
          <span className="okline">
            <CheckIcon className="ico s14" />
            {outcomeLine(s.details)}
          </span>
          <div>
            <Undo ctx={ctx} k="bank" label="Change outcome" />
          </div>
        </div>
      ) : (
        <>
          <div className="bankline">
            <BankIcon className="ico s14" />
            <span>
              <b>{bank?.name || 'The mortgagee'}</b>
            </span>
            <span className="tag t-amber">Needs setup</span>
            <span className="t11 faint">
              From the dec page. How this bank takes proof joins with Settings, Banks.
            </span>
          </div>
          {others.length > 0 && (
            <p className="m0 t12 muted">
              Also on the dec page: {others.map((m) => m.name).join(', ')}.
            </p>
          )}
          {v.bank.unread && v.bank.mortgagees.length === 0 && (
            <div className="anote">
              <AlertTriangleIcon className="ico s14" />
              <span>
                The dec page could not be read, so the portal does not know if it has a mortgagee or
                lienholder. Check the PDF: if there is none, pick Not needed.
              </span>
            </div>
          )}
          {v.bank.agencyBilled && (
            <div className="anote">
              <InfoIcon className="ico s14" />
              <span>Agency Billed: create the invoice in AMS360 and upload it.</span>
            </div>
          )}
          {waiting && (
            <div className="nbform">
              <div className="amsline">
                <AlertTriangleIcon className="ico s14" />
                <span>Update the mortgagee in AMS360 with the new bank from the insured</span>
                <a
                  className="linkbtn"
                  href={v.links.customer}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in AMS360
                </a>
                <span className="ml-auto">
                  <label className="ckline">
                    <input
                      type="checkbox"
                      checked={updated}
                      disabled={!v.canWork}
                      onChange={() => setUpdated(!updated)}
                    />
                    Updated in AMS360
                  </label>
                </span>
              </div>
            </div>
          )}
          <dl className="cprows">
            {v.bank.rows.map((row, i) => (
              <div key={row.label} className="cprow">
                <dt>{row.label}</dt>
                <dd className={row.value ? 'mono' : 'faint'}>
                  {row.value ?? 'Not found, type it from the dec page'}
                </dd>
                {row.value ? (
                  <CopyButton label={row.label} value={row.value} k={`b${i}`} c={c} />
                ) : (
                  <span />
                )}
              </div>
            ))}
          </dl>
          {borrowerLine(v) && <p className="m0 t12 text2">{borrowerLine(v)}</p>}
          <div className="sendbox">
            <div className="row g8 wrap">
              <a
                className="btn btn-sm"
                href={myCoverageInfoUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLinkIcon className="ico s12" />
                Open MyCoverageInfo
              </a>
              {mci.loan && (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() =>
                    void c.copy('mci', mciClipboard(mci), 'Loan number, zip and last name')
                  }
                >
                  <CopyIcon className="ico s12" />
                  {c.copied === 'mci' ? 'Copied' : 'Copy for MyCoverageInfo'}
                </button>
              )}
              {/* The same line with the other last name, for the bookmark (Rachy, Nov 1). */}
              {mci.loan && v.bank.borrower?.other && (
                <button
                  type="button"
                  className="linkbtn"
                  onClick={() =>
                    void c.copy(
                      'mci2',
                      mciClipboard({ ...mci, lastName: v.bank.borrower!.other!.name }),
                      `Loan number, zip and ${v.bank.borrower!.other!.name}`,
                    )
                  }
                >
                  {c.copied === 'mci2' ? 'Copied' : 'Copy the other name'}
                </button>
              )}
              {v.bank.files.map((f) => (
                <a
                  key={f.id}
                  className="btn btn-sm"
                  href={api.documentUrl(f.id, true)}
                  download={f.fileName}
                >
                  <DownloadIcon className="ico s12" />
                  {f.kind === 'invoice' ? 'Download invoice' : 'Download dec page'}
                </a>
              ))}
            </div>
            {mci.loan && <FillBookmark />}
            <span className="t12 muted">{bankFilesNote(v.bank.sendsInvoice)}</span>
          </div>
          {v.canWork && (
            <div>
              <div className="flab">
                Outcome <span className="req">*</span>
              </div>
              <div className="ocards" role="radiogroup" aria-label="Outcome">
                {choices.map((o) => (
                  <div key={o} className={outcome === o ? 'ocard on' : 'ocard'}>
                    <button
                      type="button"
                      className="oc-h"
                      role="radio"
                      aria-checked={outcome === o}
                      onClick={() => {
                        setOutcome(o);
                        if (o === 'not_needed' && v.bank.unread && !bank && !reason.trim()) {
                          setReason('No mortgagee on the dec page');
                        }
                      }}
                    >
                      <span className="rdot" />
                      <span className="col g2">
                        <span className="t13 w5 ink">{outcomeLabels[o].title}</span>
                        <span className="t12 muted">{outcomeSub(o, v.bank.sendsInvoice)}</span>
                      </span>
                    </button>
                    {outcome === o && o === 'mycoverageinfo' && (
                      <div className="oc-b">
                        <label className="t12 text2" htmlFor="mci-date">
                          Submitted on
                        </label>
                        <input
                          id="mci-date"
                          type="date"
                          className="input"
                          max={today}
                          value={date}
                          aria-invalid={date && !typedDate ? 'true' : 'false'}
                          onChange={(e) => setDate(e.target.value)}
                        />
                      </div>
                    )}
                    {outcome === o && o === 'loan_not_found' && (
                      <div className="oc-b">
                        <span className="t12 text2">
                          Email the insured with the Wrong bank information template (step 3, Mark
                          sent again). This step waits until they answer.
                        </span>
                      </div>
                    )}
                    {outcome === o && o === 'not_needed' && (
                      <div className="oc-b col">
                        <label className="t12 text2" htmlFor="nn-reason">
                          Reason
                        </label>
                        <textarea
                          id="nn-reason"
                          className="input"
                          rows={2}
                          style={{ height: 'auto' }}
                          maxLength={300}
                          placeholder="For example: loan paid off"
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
          {v.canWork && (
            <div className="row endj">
              <button
                type="button"
                className={ready ? 'btn btn-primary' : 'btn'}
                disabled={!ready}
                onClick={() => void save()}
              >
                {outcome === 'loan_not_found' ? 'Save, waiting on the insured' : 'Mark step done'}
              </button>
            </div>
          )}
          {waiting && (
            <div>
              <Undo ctx={ctx} k="bank" label="Undo Loan not found" />
            </div>
          )}
        </>
      )}
    </StepShell>
  );
}

/** Untick the last step (a link button, with the reason in a tooltip when it cannot). */
function Undo({ ctx, k, label }: { ctx: Ctx; k: StepKey; label: string }) {
  const tip = undoTip(ctx.view, k);
  const btn = (
    <button type="button" className="btn btn-sm" disabled={!!tip} onClick={() => void ctx.undo(k)}>
      {label}
    </button>
  );
  return tip ? (
    <Tip text={tip} focusable>
      {btn}
    </Tip>
  ) : (
    btn
  );
}

// ---------- step 6 ----------

function DoneStep({ ctx, onClose, onPaid }: { ctx: Ctx; onClose: () => void; onPaid: () => void }) {
  const v = ctx.view;
  const s = v.steps.done;
  return (
    <StepShell ctx={ctx} k="done">
      {s.state === 'done' ? (
        <div className="okbox">
          <span className="okline">
            <CheckIcon className="ico s14" />
            {closedLine(s.details)}
          </span>
          {anywayReason(s.details) && (
            <p className="m0 t12 text2">Reason: {anywayReason(s.details)}</p>
          )}
          {v.close.savedComment && (
            <p className="m0 t12 text2">
              Sheet comment: <span className="mono">{v.close.savedComment}</span>
            </p>
          )}
          <div className="row g8 wrap">
            {v.close.renewalStatus === 'need_payment' && v.canWork && (
              <button type="button" className="btn btn-sm btn-primary" onClick={onPaid}>
                Premium received
              </button>
            )}
            <Undo ctx={ctx} k="done" label="Reopen" />
          </div>
        </div>
      ) : (
        v.canWork && (
          <div className="row endj">
            <button
              type="button"
              className="btn btn-primary"
              aria-haspopup="dialog"
              onClick={onClose}
            >
              Close renewal
            </button>
          </div>
        )
      )}
    </StepShell>
  );
}

export function CloseDialog({
  view,
  renewal,
  onClose,
  onConfirm,
}: {
  view: StepsView;
  renewal: RenewalDetail;
  onClose: () => void;
  /** `reason` only when steps are still open: the renewal closes anyway (S16). */
  onConfirm: (
    status: 'done' | 'need_payment',
    sheetComment: string,
    reason?: string,
  ) => Promise<void>;
}) {
  const [status, setStatus] = useState<'done' | 'need_payment'>(view.close.status);
  const [comment, setComment] = useState(view.close.sheetComment);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Steps still open (Close renewal from the "..." menu): a reason, then the Close anyway confirm.
  const missing = missingOf(view);
  const anyway = missing.length > 0;
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const submit = async () => {
    setBusy(true);
    await onConfirm(status, comment, anyway ? reason.trim() : undefined);
    setBusy(false);
    setConfirming(false);
  };
  const rows: { title: string; who: string; ok: boolean }[] = [
    { title: '1. Dec page', who: decPageSummary(view), ok: decDone(view) },
    ...shownSteps(view)
      .filter((k) => k !== 'done')
      .map((k) => {
        const s = view.steps[k];
        return {
          title: `${stepNumberOf(view, k)}. ${stepTitle(k, view.amsStep)}`,
          who: s.state === 'done' ? stepStamp(s) : 'Not done',
          ok: s.state === 'done',
        };
      }),
  ];
  const subline = [
    renewal.insured,
    renewal.carrier ?? 'Carrier',
    renewal.line,
    renewal.policyNumber,
  ]
    .filter(Boolean)
    .join(' | ');
  const ready = !!comment.trim() && (!anyway || !!reason.trim()) && !busy;
  return (
    <>
      <Dialog
        id="close-t"
        title="Close renewal"
        subline={subline}
        onClose={onClose}
        footer={
          <>
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={ready ? 'btn btn-primary ml-auto' : 'btn ml-auto'}
              disabled={!ready}
              onClick={() => (anyway ? setConfirming(true) : void submit())}
            >
              {status === 'need_payment' ? 'Mark Need payment' : 'Close as Done'}
            </button>
          </>
        }
      >
        {anyway && (
          <div className="anote">
            <AlertTriangleIcon className="ico s14" />
            <span>{missingNote(missing)}</span>
          </div>
        )}
        <div>
          <div className="flab">Steps</div>
          <div className="sumlist">
            {rows.map((r) => (
              <div key={r.title} className="sumrow">
                <span className={r.ok ? 'rdot ok' : 'rdot'}>
                  {r.ok && <CheckIcon className="ico s12" />}
                </span>
                <span className="t13 ink grow">{r.title}</span>
                <span className="t12 muted ell">{r.who}</span>
              </div>
            ))}
          </div>
        </div>
        {view.close.payFirst && (
          <div>
            <div className="flab">Status</div>
            <div className="ropts" role="radiogroup" aria-label="Status">
              {(
                [
                  ['done', 'Done'],
                  ['need_payment', 'Need payment'],
                ] as const
              ).map(([k, label]) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  className={status === k ? 'ropt on' : 'ropt'}
                  aria-checked={status === k}
                  onClick={() => setStatus(k)}
                >
                  <span className="rdot" />
                  <span className="t13 ink">{label}</span>
                </button>
              ))}
            </div>
            {status === 'need_payment' && (
              <div className="anote calm" style={{ marginTop: 'var(--space-8)' }}>
                <InfoIcon className="ico s14" />
                <span>{payFirstNote(view.close.payFirst)}</span>
              </div>
            )}
          </div>
        )}
        <div>
          <label htmlFor="done-sheet" className="flab">
            Sheet comment <span className="req">*</span>
          </label>
          <div className="cprow2">
            <input
              id="done-sheet"
              className="input mono"
              maxLength={300}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
            <button
              type="button"
              className="ibtn"
              aria-label="Copy sheet comment"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(comment);
                  setCopied(true);
                } catch {
                  setCopied(false);
                }
              }}
            >
              {copied ? <span className="cpd">Copied</span> : <CopyIcon className="ico s14" />}
            </button>
          </div>
        </div>
        {anyway && (
          <div>
            <label htmlFor="done-reason" className="flab">
              Reason <span className="req">*</span>
            </label>
            <textarea
              id="done-reason"
              className="input"
              maxLength={300}
              placeholder="Why it closes before every step is done"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <p className="fnote">Kept in the Portal log with the close.</p>
          </div>
        )}
      </Dialog>
      {confirming && (
        <ConfirmPopup
          title={closeAnywayTitle(renewal.insured)}
          line={closeAnywayLine(missing)}
          action="Close anyway"
          busy={busy}
          onConfirm={() => void submit()}
          onCancel={() => setConfirming(false)}
        />
      )}
    </>
  );
}
